import { crc16Ccitt, formatCrc } from './crc';
import {
  inputModeAspectMetadataForSlot,
  inputModeByteRangesForSettingPath,
  inputModeDefaultAspectCorrection,
  inputModeDefaultSampleRateForSlot,
} from './inputModes';
import { RT4K_FILE_PATH_BYTES, RT4K_PORT_PAD, rt4kArrayElementOffset, rt4kFieldOffset } from './layout';
import {
  optimalModeByteRangesForSettingPath,
  optimalModeDecimationFactorForSlot,
  optimalModeDefaultAspectCorrection,
} from './optimalModes';
import { LUMACODE_PRESET_PATH, SETTING_DEFINITIONS, SETTINGS_BY_PATH, type ByteRange, type SettingDefinition } from './schema';
import { decodeCp437, encodeCp437 } from './textEncoding';

export const RT4K_HEADER = 'RT4K Profile';
export const RT4K_PROFILE_SIZE_196 = 23004;
export const CRC_WRITE_OFFSET = 0x20;
export const CRC_DATA_START_OFFSET = 0x80;
const SMPTE_2084_PQ_PATH = 'advanced.color.gamma_pq.smpte_2084_pq';
const INPUT_PATH = 'input';
const RGB_ADC_SOG_THRESHOLD_PATH = 'advanced.acquisition.rgb_adc.sync.sog_threshold';
const OUTPUT_COLORIMETRY_PATH = 'output.transmitter.colorimetry';
const OUTPUT_RESOLUTION_PATH = 'output.resolution';
const OUTPUT_CUSTOM_MODELINE_CUSTOM_3_PATH = 'output.custom_modelines.custom_3';
const OUTPUT_CUSTOM_MODELINE_CUSTOM_4_PATH = 'output.custom_modelines.custom_4';
const OUTPUT_HDR_PATH = 'output.transmitter.hdr';
const AUDIO_PREAMP_GAIN_PATH = 'advanced.acquisition.audio_input.sampling.preamp_gain';
const AUDIO_PREAMP_BALANCE_PATH = 'advanced.acquisition.audio_input.sampling.preamp_balance';
const AUDIO_SAMPLE_RATE_PATH = 'advanced.acquisition.audio_input.sampling.sample_rate';
const AUDIO_INPUT_OVERRIDE_PATH = 'advanced.acquisition.audio_input.source.input_override';
const AUDIO_INPUT_SWAP_PATH = 'advanced.acquisition.audio_input.source.input_swap';
const OSD_LOAD_BANNER_PATH = 'advanced.system.osd_firmware.banner_image.load_banner';
const HDMI_INPUT_DECIMATION_INPUT_PIXELS_PATH = 'advanced.acquisition.hdmi_receiver.input_decimation.input_pixels';
const HDMI_INPUT_DECIMATION_INITIAL_PHASE_PATH = 'advanced.acquisition.hdmi_receiver.input_decimation.initial_phase';
const HDMI_INPUT_DECIMATION_OUTPUT_PIXELS_PATH = 'advanced.acquisition.hdmi_receiver.input_decimation.output_pixels';
const RGB_ADC_ANTI_ALIAS_LPF_PATH = 'advanced.acquisition.rgb_adc.sampling.anti_alias_lpf';
const RGB_ADC_DECIMATION_FACTOR_PATH = 'advanced.acquisition.rgb_adc.sampling.decimation_factor';
const RGB_ADC_DECIMATION_PHASE_PATH = 'advanced.acquisition.rgb_adc.sampling.decimation_phase';
const RGB_ADC_GAIN_BLUE_PATH = 'advanced.acquisition.rgb_adc.gain.blue';
const RGB_ADC_GAIN_GREEN_PATH = 'advanced.acquisition.rgb_adc.gain.green';
const RGB_ADC_GAIN_RED_PATH = 'advanced.acquisition.rgb_adc.gain.red';
const RGB_ADC_SAMPLES_PER_LINE_PATH = 'advanced.acquisition.rgb_adc.sampling.samples_per_line';
const RGB_ADC_SUB_PHASE_PATH = 'advanced.acquisition.rgb_adc.sampling.sub_phase';
const SAMPLE_RATE_AUTO_DECIMATION_PHASE_PATH =
  'advanced.acquisition.sample_rate_detection.control.auto_decimation_phase';
const SDP_CONTRAST_PATH = 'advanced.acquisition.sdp.gain_balance.contrast';
const INTERPOLATION_HORIZONTAL_KERNEL_PATH = 'advanced.effects.interpolation.horizontal_kernel';
const INTERPOLATION_VERTICAL_KERNEL_PATH = 'advanced.effects.interpolation.vertical_kernel';
const GAMMA_PQ_INPUT_FACTOR_PATH = 'advanced.color.gamma_pq.input_factor';
const GAMMA_PQ_OUTPUT_FACTOR_PATH = 'advanced.color.gamma_pq.output_factor';
const CUSTOM_MATRIX_PATH = 'advanced.color.csc.custom_matrix';
const SCALER_ASPECT_CORRECTION_PATH = 'advanced.scaling.scaler.aspect_correction';
const SCALER_SCALING_MODE_PATH = 'advanced.scaling.scaler.scaling_mode';
const SCALER_HORIZONTAL_FACTOR_PATH = 'advanced.scaling.scaler.horizontal_factor';
const SCALER_VERTICAL_FACTOR_PATH = 'advanced.scaling.scaler.vertical_factor';
const PROFILE_CREATED_ON_DEVICE_ID_PATH = 'profiles.load_profile.created_on_device_id';
const OUTPUT_HDR_OFFSET = 0x02d0;
const OUTPUT_COLORIMETRY_OFFSET = 0x1ec8;
const HDMI_INPUT_DECIMATION_INPUT_PIXELS_OFFSET = 0x0ee9;
const HDMI_INPUT_DECIMATION_INITIAL_PHASE_OFFSET = 0x0f69;
const HDMI_INPUT_DECIMATION_OUTPUT_PIXELS_OFFSET = 0x18f9;
const CUSTOM_MATRIX_OFFSET = 0x1ec4;
const HDR_GAMMA_INPUT_FACTOR_OFFSET = 0x02c4;
const CSC_GAMMA_INPUT_FACTOR_OFFSET = rt4kFieldOffset('input_gamma_sdr_csc');
const CSC_GAMMA_OUTPUT_FACTOR_OFFSET = rt4kFieldOffset('output_gamma_sdr_csc');
const SCANLINE_FUNCTION_OFFSET = 0x0080;
const OUTPUT_HDR_OFF_RAW = 0;
const OUTPUT_HDR_HLG_RAW = 2;
const RGB_ADC_SOG_THRESHOLD_RAW_OFFSET = 11;
const RGB_ADC_SOG_THRESHOLD_STEP_MV = 350 / 31;
const OUTPUT_HDR_ENABLED_LABELS = new Set(['hdr10 [8-bit]', 'hlg [8-bit]']);
const OUTPUT_COLORIMETRY_SDR_CSC_RAW_VALUES = new Set([1, 2, 3, 4]);
const OUTPUT_COLORIMETRY_SDR_CSC_LABELS = new Set(['rec.709', 'rec.2020', 'adobe rgb', 'display-p3']);
const INPUT_SOURCE_ENHANCED_SVIDEO_RAW = 27;
const HDR_GAMMA_INPUT_FACTOR_BASE = 2.4;
const SCALER_MODE_FREE_FORM_RAW = 2;
const OUTPUT_CUSTOM_MODELINE_SLOT_BY_RESOLUTION = new Map([
  ['Custom 3', 0],
  ['Custom 4', 1],
]);
const AUDIO_PORT_HD15 = 0;
const AUDIO_PORT_RCA = 1;
const AUDIO_PORT_SCART = 2;
const AUDIO_PORT_FRONT = 3;
const AUDIO_PORT_HDMI = 4;
const AUDIO_PORT_SPDIF = 5;
const AUDIO_OVERRIDE_TO_PORT = new Map([
  [1, AUDIO_PORT_RCA],
  [2, AUDIO_PORT_HD15],
  [3, AUDIO_PORT_SCART],
  [4, AUDIO_PORT_FRONT],
  [5, AUDIO_PORT_SPDIF],
]);

type GammaPqFactorBank = 'normal' | 'sdrCsc' | 'hdr';
type RgbAdcContextKind = '240p' | '480i' | '288p' | '576i';

interface RgbAdcDisplayContext {
  kind: RgbAdcContextKind;
  scalerModeIndex: number | null;
  optModeIndex: number;
  sampleRate: number | null;
  decimationFactor: number;
  decimationPhase: number;
  decimationFactorDisplayValue: string;
  decimationPhaseDisplayValue: string;
  decimationFactorDisplayInactive: boolean;
  decimationPhaseDisplayInactive: boolean;
  subPhaseRaw: number;
  antiAliasLabel: string | null;
  scalerInputH: number | null;
  verticalPrescaleLabel: string | null;
}

export type SettingPrimitive = string | number | boolean;

export interface DecodedSetting {
  definition: SettingDefinition;
  rawBytes: Uint8Array;
  value: SettingPrimitive;
  displayValue: string;
  available: boolean;
  recognized: boolean;
}

export interface DecodedProfile {
  fileName: string;
  bytes: Uint8Array;
  size: number;
  inputModeSlot: number | null;
  optModeSlot: number | null;
  headerValid: boolean;
  storedCrc: number;
  computedCrc: number;
  crcValid: boolean;
  settings: DecodedSetting[];
  warnings: string[];
}

export interface SettingChange {
  path: string;
  label: string;
  before: string;
  after: string;
}

export interface ParseRt4ProfileOptions {
  inputModeSlot?: number | null;
  optModeSlot?: number | null;
}

export interface ApplyProfileChangesOptions {
  inputModeSlot?: number | null;
  optModeSlot?: number | null;
}

export class Rt4ProfileError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'Rt4ProfileError';
  }
}

export function parseRt4Profile(
  source: ArrayBuffer | Uint8Array,
  fileName = 'profile.rt4',
  options: ParseRt4ProfileOptions = {},
): DecodedProfile {
  const bytes = source instanceof Uint8Array ? new Uint8Array(source) : new Uint8Array(source);
  const warnings: string[] = [];
  const inputModeSlot = options.inputModeSlot ?? null;
  const optModeSlot = options.optModeSlot ?? null;

  if (bytes.length < CRC_DATA_START_OFFSET) {
    throw new Rt4ProfileError('File is too small to be an RT4K profile.');
  }

  const header = decodeFixedAscii(bytes.slice(0, RT4K_HEADER.length));
  if (header !== RT4K_HEADER) {
    throw new Rt4ProfileError(`Invalid RT4K profile header: ${header || 'empty'}.`);
  }

  const unavailableSettings = SETTING_DEFINITIONS.filter(
    (definition) => missingByteRanges(definition, bytes, inputModeSlot, optModeSlot).length > 0,
  );
  if (unavailableSettings.length > 0) {
    warnings.push(
      `${unavailableSettings.length} known settings include byte ranges beyond this ${bytes.length}-byte profile and will be shown as unavailable.`,
    );
  }

  const storedCrc = readUint16LE(bytes, CRC_WRITE_OFFSET);
  const computedCrc = crc16Ccitt(bytes, CRC_DATA_START_OFFSET);
  const settings = SETTING_DEFINITIONS.map((definition) => readSetting(definition, bytes, inputModeSlot, optModeSlot));

  if (storedCrc !== computedCrc) {
    warnings.push(`Stored CRC ${formatCrc(storedCrc)} does not match computed CRC ${formatCrc(computedCrc)}.`);
  }

  return {
    fileName,
    bytes,
    size: bytes.length,
    inputModeSlot,
    optModeSlot,
    headerValid: true,
    storedCrc,
    computedCrc,
    crcValid: storedCrc === computedCrc,
    settings,
    warnings,
  };
}

export function applyProfileChanges(
  profile: DecodedProfile,
  changes: Record<string, SettingPrimitive>,
  options: ApplyProfileChangesOptions = {},
): DecodedProfile {
  const nextBytes = new Uint8Array(profile.bytes);
  const gammaPqFactorBank = activeGammaPqFactorBank(nextBytes, changes);
  const inputModeSlot = options.inputModeSlot === undefined ? profile.inputModeSlot : options.inputModeSlot;
  const optModeSlot = options.optModeSlot === undefined ? profile.optModeSlot : options.optModeSlot;

  for (const [path, value] of Object.entries(changes)) {
    const definition = SETTINGS_BY_PATH.get(path);
    if (!definition) {
      throw new Rt4ProfileError(`Unsupported setting: ${path}`);
    }
    if (!definition.writable) {
      throw new Rt4ProfileError(`Setting is read-only: ${path}`);
    }

    const encoded = encodeSettingValueForProfile(definition, value, gammaPqFactorBank, inputModeSlot, optModeSlot, nextBytes);
    writeByteRanges(
      nextBytes,
      writeByteRangesForProfile(definition, gammaPqFactorBank, inputModeSlot, optModeSlot, nextBytes, changes),
      encoded,
    );
  }

  validateHdmiInputDecimationChanges(nextBytes, changes, inputModeSlot);

  return parseRt4Profile(nextBytes, profile.fileName, { inputModeSlot, optModeSlot });
}

export function profileByteRangesForChange(
  profile: DecodedProfile,
  path: string,
  value: SettingPrimitive,
  options: ApplyProfileChangesOptions = {},
): ByteRange[] {
  const definition = SETTINGS_BY_PATH.get(path);
  if (!definition) {
    throw new Rt4ProfileError(`Unsupported setting: ${path}`);
  }
  if (!definition.writable) {
    throw new Rt4ProfileError(`Setting is read-only: ${path}`);
  }

  const changes = { [path]: value };
  const inputModeSlot = options.inputModeSlot === undefined ? profile.inputModeSlot : options.inputModeSlot;
  const optModeSlot = options.optModeSlot === undefined ? profile.optModeSlot : options.optModeSlot;
  const gammaPqFactorBank = activeGammaPqFactorBank(profile.bytes, changes);

  return writeByteRangesForProfile(
    definition,
    gammaPqFactorBank,
    inputModeSlot,
    optModeSlot,
    profile.bytes,
    changes,
  );
}

// Fields consulted when choosing a write address, encoding, or validating a
// setting. Live editing reads these first so cached state cannot select the
// wrong gamma/scaler/audio bank or validate against an old HDMI phase limit.
export function profileChangeContextByteRanges(
  path: string,
  inputModeSlot: number | null,
  optModeSlot: number | null,
): ByteRange[] {
  if (isGammaPqFactorPath(path)) {
    return [
      { address: OUTPUT_HDR_OFFSET, length: 1 },
      { address: OUTPUT_COLORIMETRY_OFFSET, length: 1 },
      { address: CUSTOM_MATRIX_OFFSET, length: 1 },
    ];
  }
  if (path === SCALER_VERTICAL_FACTOR_PATH) {
    return optimalModeByteRangesForSettingPath(SCALER_SCALING_MODE_PATH, optModeSlot)
      ?? inputModeByteRangesForSettingPath(SCALER_SCALING_MODE_PATH, inputModeSlot)
      ?? [];
  }
  if ([AUDIO_PREAMP_GAIN_PATH, AUDIO_PREAMP_BALANCE_PATH, AUDIO_INPUT_OVERRIDE_PATH, AUDIO_INPUT_SWAP_PATH].includes(path)) {
    return [
      { address: rt4kFieldOffset('input_port'), length: 1 },
      { address: rt4kFieldOffset('audio_override'), length: RT4K_PORT_PAD },
    ];
  }
  if ([HDMI_INPUT_DECIMATION_INPUT_PIXELS_PATH, HDMI_INPUT_DECIMATION_INITIAL_PHASE_PATH].includes(path)) {
    return [HDMI_INPUT_DECIMATION_INPUT_PIXELS_PATH, HDMI_INPUT_DECIMATION_INITIAL_PHASE_PATH].flatMap((dependency) =>
      inputModeByteRangesForSettingPath(dependency, inputModeSlot) ?? SETTINGS_BY_PATH.get(dependency)!.byteRanges,
    );
  }
  return [];
}

export function exportRt4Profile(profile: DecodedProfile): Uint8Array {
  const bytes = new Uint8Array(profile.bytes);
  const crc = crc16Ccitt(bytes, CRC_DATA_START_OFFSET);
  bytes[CRC_WRITE_OFFSET] = crc & 0xff;
  bytes[CRC_WRITE_OFFSET + 1] = (crc >> 8) & 0xff;
  return bytes;
}

export function getChangedSettings(original: DecodedProfile, draft: DecodedProfile): SettingChange[] {
  const originalByPath = new Map(original.settings.map((setting) => [setting.definition.path, setting]));

  return draft.settings.reduce<SettingChange[]>((changes, setting) => {
    const originalSetting = originalByPath.get(setting.definition.path);
    if (
      !originalSetting ||
      (valuesEqual(originalSetting.value, setting.value) &&
        setting.definition.path !== SAMPLE_RATE_AUTO_DECIMATION_PHASE_PATH) ||
      bytesEqual(originalSetting.rawBytes, setting.rawBytes)
    ) {
      return changes;
    }

    changes.push({
      path: setting.definition.path,
      label: setting.definition.label,
      before: setting.definition.path === SAMPLE_RATE_AUTO_DECIMATION_PHASE_PATH && originalSetting.rawBytes[0] === 0
        ? 'Off (default)'
        : originalSetting.displayValue,
      after: setting.displayValue,
    });
    return changes;
  }, []);
}

export function getDownloadFileName(fileName: string): string {
  const extensionMatch = fileName.match(/\.(?:rt4|rt6)$/i);
  const extension = extensionMatch?.[0] ?? '.rt4';
  const baseName = extensionMatch
    ? fileName.slice(0, -extension.length)
    : fileName;
  return `${baseName || 'profile'}-modified${extension}`;
}

export function readSetting(
  definition: SettingDefinition,
  bytes: Uint8Array,
  inputModeSlot: number | null = null,
  optModeSlot: number | null = null,
): DecodedSetting {
  const byteRanges = byteRangesForProfile(definition, bytes, inputModeSlot, optModeSlot);
  const explicitInputModeSlot = inputModeSlot !== null;
  const missingRanges = missingByteRanges(definition, bytes, inputModeSlot, optModeSlot);
  if (missingRanges.length > 0) {
    const availableRawBytes = readAvailableByteRanges(bytes, byteRanges);
    if (definition.type === 'enum' && availableRawBytes.length > 0) {
      const partialOption = matchPartialEnumOption(definition, availableRawBytes);
      if (partialOption) {
        return {
          definition,
          rawBytes: availableRawBytes,
          value: partialOption.label,
          displayValue: partialOption.label,
          available: true,
          recognized: true,
        };
      }
    }

    const displayValue = `Unavailable (${missingRanges.map(formatByteRange).join('; ')})`;
    return {
      definition,
      rawBytes: new Uint8Array(),
      value: displayValue,
      displayValue,
      available: false,
      recognized: false,
    };
  }

  const rawBytes = readByteRanges(bytes, byteRanges);

  switch (definition.type) {
    case 'enum': {
      if (definition.path === OUTPUT_RESOLUTION_PATH) {
        return readOutputResolution(definition, bytes, rawBytes);
      }
      if (definition.path === OUTPUT_COLORIMETRY_PATH) {
        return readOutputColorimetry(definition, bytes, rawBytes);
      }
      if (definition.path === SCALER_ASPECT_CORRECTION_PATH) {
        const contextualSetting = readSlotAspectCorrection(definition, rawBytes, inputModeSlot, optModeSlot);
        if (contextualSetting) {
          return contextualSetting;
        }
      }
      if (definition.path === RGB_ADC_DECIMATION_FACTOR_PATH && optModeSlot !== null) {
        const contextualSetting = readOptimalModeDecimationFactor(definition, rawBytes, optModeSlot);
        if (contextualSetting) {
          return contextualSetting;
        }
      }
      if (
        definition.path === RGB_ADC_DECIMATION_FACTOR_PATH ||
        definition.path === RGB_ADC_ANTI_ALIAS_LPF_PATH ||
        definition.path === SAMPLE_RATE_AUTO_DECIMATION_PHASE_PATH
      ) {
        const contextualSetting = explicitInputModeSlot
          ? null
          : readRgbAdcContextualEnum(definition, bytes, rawBytes);
        if (contextualSetting) {
          return contextualSetting;
        }
      }
      if (definition.path === INTERPOLATION_HORIZONTAL_KERNEL_PATH || definition.path === INTERPOLATION_VERTICAL_KERNEL_PATH) {
        const contextualSetting = readInterpolationKernel(definition, bytes, rawBytes, explicitInputModeSlot);
        if (contextualSetting) {
          return contextualSetting;
        }
      }
      const option = matchEnumOption(definition, rawBytes);
      if (option) {
        return {
          definition,
          rawBytes,
          value: option.label,
          displayValue: option.label,
          available: true,
          recognized: true,
        };
      }

      const displayValue = `Unknown (${toHex(rawBytes)})`;
      return {
        definition,
        rawBytes,
        value: displayValue,
        displayValue,
        available: true,
        recognized: false,
      };
    }
    case 'bit': {
      const value = rawBytes[0] !== 0;
      return {
        definition,
        rawBytes,
        value,
        displayValue: value ? 'On' : 'Off',
        available: true,
        recognized: rawBytes[0] === 0 || rawBytes[0] === 1,
      };
    }
    case 'uint8':
    case 'int8':
    case 'uint16':
    case 'int16':
    case 'uint32':
    case 'int32':
    case 'float32': {
      if (definition.path === RGB_ADC_SAMPLES_PER_LINE_PATH) {
        const sampleRateSetting = readRgbAdcSamplesPerLine(definition, rawBytes, inputModeSlot);
        if (sampleRateSetting) {
          return sampleRateSetting;
        }
      }
      if (
        definition.path === RGB_ADC_SAMPLES_PER_LINE_PATH ||
        definition.path === RGB_ADC_DECIMATION_PHASE_PATH ||
        definition.path === RGB_ADC_SUB_PHASE_PATH
      ) {
        const contextualSetting = explicitInputModeSlot
          ? null
          : readRgbAdcContextualNumeric(definition, bytes, rawBytes);
        if (contextualSetting) {
          return contextualSetting;
        }
      }
      if (definition.path === RGB_ADC_DECIMATION_PHASE_PATH) {
        return readRgbAdcDecimationPhase(definition, bytes, rawBytes, inputModeSlot, optModeSlot);
      }
      if (definition.path === GAMMA_PQ_INPUT_FACTOR_PATH || definition.path === GAMMA_PQ_OUTPUT_FACTOR_PATH) {
        const contextualSetting = readGammaPqFactor(definition, bytes, rawBytes);
        if (contextualSetting) {
          return contextualSetting;
        }
      }
      if (
        definition.path === RGB_ADC_GAIN_RED_PATH ||
        definition.path === RGB_ADC_GAIN_GREEN_PATH ||
        definition.path === RGB_ADC_GAIN_BLUE_PATH
      ) {
        const contextualSetting = explicitInputModeSlot ? null : readRgbAdcGain(definition, bytes, rawBytes);
        if (contextualSetting) {
          return contextualSetting;
        }
      }
      if (definition.path === RGB_ADC_SOG_THRESHOLD_PATH) {
        return readRgbAdcSogThreshold(definition, rawBytes);
      }
      if (definition.path === SDP_CONTRAST_PATH) {
        const contextualSetting = readSdpContrast(definition, bytes, rawBytes);
        if (contextualSetting) {
          return contextualSetting;
        }
      }
      if (definition.path === SMPTE_2084_PQ_PATH) {
        return readSmpte2084Pq(definition, bytes, rawBytes);
      }
      if (definition.path === AUDIO_PREAMP_BALANCE_PATH) {
        return readAudioPreampBalance(definition, rawBytes);
      }
      if (
        definition.path === HDMI_INPUT_DECIMATION_INPUT_PIXELS_PATH ||
        definition.path === HDMI_INPUT_DECIMATION_INITIAL_PHASE_PATH ||
        definition.path === HDMI_INPUT_DECIMATION_OUTPUT_PIXELS_PATH
      ) {
        return readHdmiInputDecimation(definition, bytes, rawBytes, inputModeSlot);
      }
      const value = decodeNumericValue(definition, rawBytes);
      return {
        definition,
        rawBytes,
        value,
        displayValue: formatNumericValue(definition, value),
        available: true,
        recognized: true,
      };
    }
    case 'string': {
      if (definition.path === PROFILE_CREATED_ON_DEVICE_ID_PATH) {
        return readProfileCreatedOnDeviceId(definition, rawBytes);
      }
      const value = definition.path === LUMACODE_PRESET_PATH
        ? decodeCp437(rawBytes.slice(0, rawBytes.includes(0) ? rawBytes.indexOf(0) : rawBytes.length))
        : decodeCString(rawBytes);
      const displayValue =
        value ||
        (definition.path === OSD_LOAD_BANNER_PATH
          ? 'default.bmp'
          : definition.path === OUTPUT_CUSTOM_MODELINE_CUSTOM_3_PATH ||
            definition.path === OUTPUT_CUSTOM_MODELINE_CUSTOM_4_PATH
          ? ''
          : '(empty)');
      return {
        definition,
        rawBytes,
        value,
        displayValue,
        available: true,
        recognized: true,
      };
    }
  }
}

function readSlotAspectCorrection(
  definition: SettingDefinition,
  rawBytes: Uint8Array,
  inputModeSlot: number | null,
  optModeSlot: number | null,
): DecodedSetting | null {
  if (optModeSlot === null) {
    const aspectMetadata = inputModeAspectMetadataForSlot(inputModeSlot);
    if (aspectMetadata?.locked) {
      return contextualDisplaySetting(
        definition,
        rawBytes,
        aspectMetadata.defaultAspectCorrection,
        aspectMetadata.defaultAspectCorrection,
      );
    }
  }

  const defaultDisplayValue = slotDefaultAspectCorrection(inputModeSlot, optModeSlot);
  if (!defaultDisplayValue) {
    return null;
  }

  const displayValue = variableParAspectDisplayValue(rawBytes[0] ?? 0, defaultDisplayValue);
  if (!displayValue) {
    return null;
  }

  return contextualDisplaySetting(definition, rawBytes, displayValue, displayValue);
}

function slotDefaultAspectCorrection(inputModeSlot: number | null, optModeSlot: number | null): string | null {
  return optModeSlot !== null
    ? optimalModeDefaultAspectCorrection(optModeSlot)
    : inputModeDefaultAspectCorrection(inputModeSlot);
}

function variableParAspectDisplayValue(rawValue: number, defaultDisplayValue: string): string | null {
  switch (rawValue) {
    case 0:
      return defaultDisplayValue;
    case 1:
      return '16:9 (PAR)';
    case 2:
      return '1:1 (Sq. Pixel)';
    default:
      return null;
  }
}

function variableParAspectRawValue(value: SettingPrimitive, defaultDisplayValue: string): number | null {
  const label = String(value).toLowerCase();
  if (label === defaultDisplayValue.toLowerCase()) {
    return 0;
  }
  if (label === '16:9 (par)') {
    return 1;
  }
  if (label === '1:1 (sq. pixel)') {
    return 2;
  }
  return null;
}

function readOptimalModeDecimationFactor(
  definition: SettingDefinition,
  rawBytes: Uint8Array,
  optModeSlot: number,
): DecodedSetting | null {
  const factor = optimalModeDecimationFactorForSlot(optModeSlot);
  if (factor === null) {
    return null;
  }

  const displayValue = String(factor);
  return contextualDisplaySetting(definition, rawBytes, displayValue, displayValue, false);
}

function readRgbAdcContextualEnum(
  definition: SettingDefinition,
  bytes: Uint8Array,
  rawBytes: Uint8Array,
): DecodedSetting | null {
  const context = rgbAdcDisplayContext(bytes);
  if (!context) {
    return null;
  }

  if (definition.path === RGB_ADC_DECIMATION_FACTOR_PATH) {
    return contextualDisplaySetting(
      definition,
      rawBytes,
      context.decimationFactorDisplayValue,
      context.decimationFactorDisplayValue,
      !context.decimationFactorDisplayInactive,
    );
  }

  if (definition.path === RGB_ADC_ANTI_ALIAS_LPF_PATH && context.antiAliasLabel !== null) {
    return contextualDisplaySetting(definition, rawBytes, context.antiAliasLabel, context.antiAliasLabel, false);
  }

  if (definition.path === SAMPLE_RATE_AUTO_DECIMATION_PHASE_PATH && rawBytes[0] === 1) {
    const displayValue = `Auto (${context.decimationPhase})`;
    return contextualDisplaySetting(definition, rawBytes, displayValue, displayValue, false);
  }

  return null;
}

function readRgbAdcContextualNumeric(
  definition: SettingDefinition,
  bytes: Uint8Array,
  rawBytes: Uint8Array,
): DecodedSetting | null {
  const context = rgbAdcDisplayContext(bytes);

  if (definition.path === RGB_ADC_SAMPLES_PER_LINE_PATH) {
    const enhancedSVideoSetting = readEnhancedSVideoSamplesPerLine(definition, bytes, rawBytes, context);
    if (enhancedSVideoSetting) {
      return enhancedSVideoSetting;
    }

    if (context?.sampleRate !== null && context?.sampleRate !== undefined) {
      return contextualDisplaySetting(definition, rawBytes, context.sampleRate, formatNumericValue(definition, context.sampleRate));
    }
  }

  if (!context) {
    return null;
  }

  if (definition.path === RGB_ADC_DECIMATION_PHASE_PATH) {
    return contextualDisplaySetting(
      definition,
      rawBytes,
      context.decimationPhase,
      context.decimationPhaseDisplayValue,
      !context.decimationPhaseDisplayInactive,
    );
  }

  if (definition.path === RGB_ADC_SUB_PHASE_PATH) {
    const value = context.subPhaseRaw * 11.25;
    return contextualDisplaySetting(definition, rawBytes, value, formatNumericValue(definition, value));
  }

  return null;
}

function readRgbAdcSamplesPerLine(
  definition: SettingDefinition,
  rawBytes: Uint8Array,
  inputModeSlot: number | null,
): DecodedSetting | null {
  const defaultSampleRate = inputModeDefaultSampleRateForSlot(inputModeSlot);
  if (defaultSampleRate === null) {
    return null;
  }

  const sampleRateOffset = readInt16LE(rawBytes, 0);
  const value = defaultSampleRate + sampleRateOffset;
  const min = definition.min ?? Number.NEGATIVE_INFINITY;
  const max = definition.max ?? Number.POSITIVE_INFINITY;

  return {
    definition,
    rawBytes,
    value,
    displayValue: formatNumericValue(definition, value),
    available: true,
    recognized: value >= min && value <= max,
  };
}

function readEnhancedSVideoSamplesPerLine(
  definition: SettingDefinition,
  bytes: Uint8Array,
  rawBytes: Uint8Array,
  context: RgbAdcDisplayContext | null,
): DecodedSetting | null {
  if (!enhancedSVideoInput(bytes)) {
    return null;
  }

  const referenceIndex = enhancedSVideoSampleReferenceIndex(bytes, context);
  if (referenceIndex === null) {
    return null;
  }

  const directSampleValue = decodeNumericValue(definition, rawBytes);
  const referenceSampleValue = readInt16LE(bytes, rt4kArrayElementOffset('tvp_sr_override', referenceIndex));
  if (directSampleValue <= 0 || referenceSampleValue <= 0) {
    return null;
  }

  const value = directSampleValue + referenceSampleValue;
  return contextualDisplaySetting(definition, rawBytes, value, formatNumericValue(definition, value));
}

function readInterpolationKernel(
  definition: SettingDefinition,
  bytes: Uint8Array,
  rawBytes: Uint8Array,
  explicitInputModeSlot: boolean,
): DecodedSetting | null {
  if (
    definition.path === INTERPOLATION_VERTICAL_KERNEL_PATH &&
    bytes[SCANLINE_FUNCTION_OFFSET] !== 0 &&
    bytes[SCANLINE_FUNCTION_OFFSET] !== 6
  ) {
    return contextualDisplaySetting(definition, rawBytes, 'N/A (Scanlines)', 'N/A (Scanlines)', false);
  }

  if (explicitInputModeSlot || definition.path !== INTERPOLATION_HORIZONTAL_KERNEL_PATH) {
    return null;
  }

  const context = rgbAdcDisplayContext(bytes);
  if (!context || context.scalerModeIndex === null) {
    return null;
  }

  const rawValue = bytes[rt4kArrayElementOffset('scaler_hori_kernel', context.scalerModeIndex)];
  const option = definition.options?.find((candidate) => candidate.value[0] === rawValue);
  if (!option) {
    return null;
  }

  return contextualDisplaySetting(definition, rawBytes, option.label, option.label);
}

function readGammaPqFactor(definition: SettingDefinition, bytes: Uint8Array, rawBytes: Uint8Array): DecodedSetting | null {
  if (usesHdrGammaPqFactors(bytes)) {
    if (definition.path === GAMMA_PQ_OUTPUT_FACTOR_PATH) {
      return contextualDisplaySetting(definition, rawBytes, 'N/A', 'N/A');
    }

    const hdrRawBytes = readByteRanges(bytes, [{ address: HDR_GAMMA_INPUT_FACTOR_OFFSET, length: 4 }]);
    const rawValue = readInt32LE(bytes, HDR_GAMMA_INPUT_FACTOR_OFFSET);
    const value = rawValue * (definition.scale ?? 1) + HDR_GAMMA_INPUT_FACTOR_BASE;

    return contextualDisplaySetting(definition, hdrRawBytes, value, formatNumericValue(definition, value));
  }

  if (usesSdrCscGammaPqFactors(bytes)) {
    const offset =
      definition.path === GAMMA_PQ_INPUT_FACTOR_PATH ? CSC_GAMMA_INPUT_FACTOR_OFFSET : CSC_GAMMA_OUTPUT_FACTOR_OFFSET;
    const cscRawBytes = readByteRanges(bytes, [{ address: offset, length: 4 }]);
    const rawValue = readInt32LE(bytes, offset);
    const value = rawValue * (definition.scale ?? 1) + HDR_GAMMA_INPUT_FACTOR_BASE;

    return contextualDisplaySetting(definition, cscRawBytes, value, formatNumericValue(definition, value));
  }

  return null;
}

function isCustomMatrixEnabled(bytes: Uint8Array): boolean {
  return (bytes[CUSTOM_MATRIX_OFFSET] ?? 0) !== 0;
}

function usesSdrCscGammaPqFactors(bytes: Uint8Array): boolean {
  return isCustomMatrixEnabled(bytes) || OUTPUT_COLORIMETRY_SDR_CSC_RAW_VALUES.has(bytes[OUTPUT_COLORIMETRY_OFFSET] ?? 0);
}

function usesHdrGammaPqFactors(bytes: Uint8Array): boolean {
  return (bytes[OUTPUT_HDR_OFFSET] ?? OUTPUT_HDR_OFF_RAW) !== OUTPUT_HDR_OFF_RAW;
}

function activeGammaPqFactorBank(bytes: Uint8Array, changes: Record<string, SettingPrimitive>): GammaPqFactorBank {
  if (shouldUseHdrGammaPqFactors(bytes, changes)) {
    return 'hdr';
  }

  return shouldUseSdrCscGammaPqFactors(bytes, changes) ? 'sdrCsc' : 'normal';
}

function shouldUseHdrGammaPqFactors(bytes: Uint8Array, changes: Record<string, SettingPrimitive>): boolean {
  if (OUTPUT_HDR_PATH in changes) {
    return OUTPUT_HDR_ENABLED_LABELS.has(String(changes[OUTPUT_HDR_PATH]).toLowerCase());
  }

  return usesHdrGammaPqFactors(bytes);
}

function shouldUseSdrCscGammaPqFactors(bytes: Uint8Array, changes: Record<string, SettingPrimitive>): boolean {
  const customMatrixEnabled =
    CUSTOM_MATRIX_PATH in changes ? coerceBoolean(changes[CUSTOM_MATRIX_PATH]) : isCustomMatrixEnabled(bytes);
  const sdrCscColorimetry =
    OUTPUT_COLORIMETRY_PATH in changes
      ? OUTPUT_COLORIMETRY_SDR_CSC_LABELS.has(String(changes[OUTPUT_COLORIMETRY_PATH]).toLowerCase())
      : OUTPUT_COLORIMETRY_SDR_CSC_RAW_VALUES.has(bytes[OUTPUT_COLORIMETRY_OFFSET] ?? 0);

  return customMatrixEnabled || sdrCscColorimetry;
}

function readRgbAdcGain(definition: SettingDefinition, bytes: Uint8Array, rawBytes: Uint8Array): DecodedSetting | null {
  const context = rgbAdcDisplayContext(bytes);
  if (context?.kind !== '480i') {
    return null;
  }

  const fieldName =
    definition.path === RGB_ADC_GAIN_RED_PATH
      ? 'tvp_gain_r_override'
      : definition.path === RGB_ADC_GAIN_GREEN_PATH
      ? 'tvp_gain_g_override'
      : 'tvp_gain_b_override';
  const rawValue = readInt16LE(bytes, rt4kArrayElementOffset(fieldName, 1));
  if (rawValue <= 0) {
    return null;
  }

  const value = rawValue * (definition.scale ?? 1) + (definition.offset ?? 0);
  return contextualDisplaySetting(definition, rawBytes, value, formatNumericValue(definition, value));
}

function rgbAdcDisplayContext(bytes: Uint8Array): RgbAdcDisplayContext | null {
  void bytes;
  return null;
}

function enhancedSVideoInput(bytes: Uint8Array): boolean {
  return (bytes[rt4kFieldOffset('input_source')] ?? 0) === INPUT_SOURCE_ENHANCED_SVIDEO_RAW;
}

function enhancedSVideoSampleReferenceIndex(bytes: Uint8Array, context: RgbAdcDisplayContext | null): number | null {
  void bytes;
  if (context) {
    return context.optModeIndex;
  }

  return null;
}

function contextualDisplaySetting(
  definition: SettingDefinition,
  rawBytes: Uint8Array,
  value: SettingPrimitive,
  displayValue: string,
  recognized = true,
): DecodedSetting {
  return {
    definition,
    rawBytes,
    value,
    displayValue,
    available: true,
    recognized,
  };
}

function readHdmiInputDecimation(
  definition: SettingDefinition,
  bytes: Uint8Array,
  rawBytes: Uint8Array,
  inputModeSlot: number | null,
): DecodedSetting {
  const rawValue = decodeRawNumericValue(definition, rawBytes);
  const inputPixelsRaw = readProfileUint8(
    bytes,
    HDMI_INPUT_DECIMATION_INPUT_PIXELS_PATH,
    inputModeSlot,
    HDMI_INPUT_DECIMATION_INPUT_PIXELS_OFFSET,
  );
  const outputPixelsRaw = readProfileUint8(
    bytes,
    HDMI_INPUT_DECIMATION_OUTPUT_PIXELS_PATH,
    inputModeSlot,
    HDMI_INPUT_DECIMATION_OUTPUT_PIXELS_OFFSET,
  );
  const isInitialPhase = definition.path === HDMI_INPUT_DECIMATION_INITIAL_PHASE_PATH;

  if (rawValue === 0 && (!isInitialPhase || (inputPixelsRaw === 0 && outputPixelsRaw === 0))) {
    return {
      definition,
      rawBytes,
      value: 'Off',
      displayValue: 'Off',
      available: true,
      recognized: true,
    };
  }

  const value = isInitialPhase ? rawValue + 1 : rawValue;
  const min = definition.min ?? Number.NEGATIVE_INFINITY;
  const max =
    isInitialPhase && inputPixelsRaw > 0 ? inputPixelsRaw : (definition.max ?? Number.POSITIVE_INFINITY);
  const displayValue =
    isInitialPhase && inputPixelsRaw > 0 ? `${value} of ${inputPixelsRaw}` : formatNumericValue(definition, value);

  return {
    definition,
    rawBytes,
    value,
    displayValue,
    available: true,
    recognized: value >= min && value <= max,
  };
}

function validateHdmiInputDecimationChanges(
  bytes: Uint8Array,
  changes: Record<string, SettingPrimitive>,
  inputModeSlot: number | null,
): void {
  if (
    !(HDMI_INPUT_DECIMATION_INPUT_PIXELS_PATH in changes) &&
    !(HDMI_INPUT_DECIMATION_INITIAL_PHASE_PATH in changes)
  ) {
    return;
  }

  const inputPixelsRaw = readProfileUint8(
    bytes,
    HDMI_INPUT_DECIMATION_INPUT_PIXELS_PATH,
    inputModeSlot,
    HDMI_INPUT_DECIMATION_INPUT_PIXELS_OFFSET,
  );
  const initialPhaseRaw = readProfileUint8(
    bytes,
    HDMI_INPUT_DECIMATION_INITIAL_PHASE_PATH,
    inputModeSlot,
    HDMI_INPUT_DECIMATION_INITIAL_PHASE_OFFSET,
  );
  const initialPhase = initialPhaseRaw + 1;
  if (inputPixelsRaw > 0 && initialPhase > inputPixelsRaw) {
    throw new Rt4ProfileError(`Initial Phase cannot exceed Input Pixels (${initialPhase} of ${inputPixelsRaw}).`);
  }
}

function readAudioPreampBalance(definition: SettingDefinition, rawBytes: Uint8Array): DecodedSetting {
  const value = decodeNumericValue(definition, rawBytes);
  let displayValue = 'Off';

  if (value < 0) {
    displayValue = `L: +${formatNumericValue(definition, Math.abs(value))}`;
  } else if (value > 0) {
    displayValue = `R: +${formatNumericValue(definition, value)}`;
  }

  return {
    definition,
    rawBytes,
    value,
    displayValue,
    available: true,
    recognized: true,
  };
}

function readRgbAdcDecimationPhase(
  definition: SettingDefinition,
  bytes: Uint8Array,
  rawBytes: Uint8Array,
  inputModeSlot: number | null,
  optModeSlot: number | null,
): DecodedSetting {
  const optFactor = optimalModeDecimationFactorForSlot(optModeSlot);
  const factorRanges = optFactor === null ? inputModeByteRangesForSettingPath(RGB_ADC_DECIMATION_FACTOR_PATH, inputModeSlot) : null;
  const factorRaw = optFactor === null ? (factorRanges ? (bytes[factorRanges[0].address] ?? 0) : (bytes[0x0e7a] ?? 0)) : optFactor - 1;
  const phaseRaw = rawBytes[0] ?? 0;

  if (factorRaw === 0) {
    return {
      definition,
      rawBytes,
      value: 'Off',
      displayValue: 'Off',
      available: true,
      recognized: phaseRaw === 0,
    };
  }

  const value = phaseRaw + 1;
  const factor = factorRaw + 1;
  return {
    definition,
    rawBytes,
    value,
    displayValue: `${value} of ${factor}`,
    available: true,
    recognized: value >= 1 && value <= factor,
  };
}

function readSmpte2084Pq(definition: SettingDefinition, bytes: Uint8Array, rawBytes: Uint8Array): DecodedSetting {
  const hdrRaw = bytes[OUTPUT_HDR_OFFSET] ?? OUTPUT_HDR_OFF_RAW;
  if (hdrRaw === OUTPUT_HDR_OFF_RAW || hdrRaw === OUTPUT_HDR_HLG_RAW) {
    return contextualDisplaySetting(definition, rawBytes, 'N/A', 'N/A');
  }

  const rawValue = decodeRawNumericValue(definition, rawBytes);
  const denominator = rawValue + 50;
  const value = denominator > 0 ? 50000 / denominator : Number.POSITIVE_INFINITY;
  const min = definition.min ?? Number.NEGATIVE_INFINITY;
  const max = definition.max ?? Number.POSITIVE_INFINITY;

  return {
    definition,
    rawBytes,
    value,
    displayValue: Number.isFinite(value) ? formatNumericValue(definition, value) : `Unknown (${toHex(rawBytes)})`,
    available: true,
    recognized: Number.isFinite(value) && value >= min && value <= max,
  };
}

function readOutputResolution(definition: SettingDefinition, bytes: Uint8Array, rawBytes: Uint8Array): DecodedSetting {
  const option = matchEnumOption(definition, rawBytes);
  if (!option) {
    const displayValue = `Unknown (${toHex(rawBytes)})`;
    return {
      definition,
      rawBytes,
      value: displayValue,
      displayValue,
      available: true,
      recognized: false,
    };
  }

  return {
    definition,
    rawBytes,
    value: option.label,
    displayValue: outputResolutionDisplayLabel(bytes, option.label),
    available: true,
    recognized: true,
  };
}

function outputResolutionDisplayLabel(bytes: Uint8Array, label: string): string {
  const customSlotIndex = OUTPUT_CUSTOM_MODELINE_SLOT_BY_RESOLUTION.get(label);
  if (customSlotIndex === undefined) {
    return label;
  }

  const modelinePath = decodeCString(
    readByteRanges(bytes, [
      {
        address: rt4kArrayElementOffset('custom_slot_file', customSlotIndex),
        length: RT4K_FILE_PATH_BYTES,
      },
    ]),
  );
  const modelineName = customModelineDisplayName(modelinePath);
  return modelineName ? `${modelineName} (${label})` : label;
}

function customModelineDisplayName(modelinePath: string): string {
  const normalized = modelinePath.trim().replaceAll('\\', '/');
  const basename = normalized.split('/').filter(Boolean).pop() ?? '';
  return basename.replace(/\.[^.]+$/, '').trim();
}

function readOutputColorimetry(definition: SettingDefinition, bytes: Uint8Array, rawBytes: Uint8Array): DecodedSetting {
  const option = matchEnumOption(definition, rawBytes);

  if (!option) {
    const displayValue = `Unknown (${toHex(rawBytes)})`;
    return {
      definition,
      rawBytes,
      value: displayValue,
      displayValue,
      available: true,
      recognized: false,
    };
  }

  const hdrRaw = bytes[OUTPUT_HDR_OFFSET] ?? 0;
  const displayValue = rawBytes[0] === 0 && hdrRaw !== 0 ? 'Auto (Rec. 2100)' : option.label;

  return {
    definition,
    rawBytes,
    value: option.label,
    displayValue,
    available: true,
    recognized: true,
  };
}

function readProfileCreatedOnDeviceId(definition: SettingDefinition, rawBytes: Uint8Array): DecodedSetting {
  const high = readUint32LE(rawBytes, 0);
  const low = readUint32LE(rawBytes, 4);
  const value = high === 0 && low === 0 ? '' : `${formatUint32Hex(high)}-${formatUint32Hex(low)}`;

  return {
    definition,
    rawBytes,
    value,
    displayValue: value || '(empty)',
    available: true,
    recognized: true,
  };
}

function formatUint32Hex(value: number): string {
  return value.toString(16).toUpperCase().padStart(8, '0');
}

function readRgbAdcSogThreshold(definition: SettingDefinition, rawBytes: Uint8Array): DecodedSetting {
  const rawValue = decodeRawNumericValue(definition, rawBytes);
  const value = roundToPrecision(
    (rawValue + RGB_ADC_SOG_THRESHOLD_RAW_OFFSET) * RGB_ADC_SOG_THRESHOLD_STEP_MV,
    definition.precision,
  );

  return {
    definition,
    rawBytes,
    value,
    displayValue: formatNumericValue(definition, value),
    available: true,
    recognized: rawValue >= -RGB_ADC_SOG_THRESHOLD_RAW_OFFSET && rawValue <= 20,
  };
}

function readSdpContrast(definition: SettingDefinition, bytes: Uint8Array, rawBytes: Uint8Array): DecodedSetting | null {
  void bytes;
  const value = decodeRawNumericValue(definition, rawBytes);
  return contextualDisplaySetting(definition, rawBytes, value, formatNumericValue(definition, value));
}

function matchEnumOption(definition: SettingDefinition, rawBytes: Uint8Array) {
  // Zero is the untouched/default auto-phase state. Preserve it on read;
  // an explicit Off edit must use 2 so firmware will not auto-enable it.
  if (definition.path === SAMPLE_RATE_AUTO_DECIMATION_PHASE_PATH && rawBytes[0] === 0) {
    return definition.options?.find((candidate) => candidate.label === 'Off');
  }
  const exactMatch = definition.options?.find((candidate) => bytesEqual(rawBytes, Uint8Array.from(candidate.value)));
  if (exactMatch) {
    return exactMatch;
  }

  if (definition.path === 'input' && rawBytes.length >= 2) {
    // Several public 1.96 profiles preserve an older/stale source selector byte at 0x0368.
    // The input mode byte at 0x5869 is still unique, so use it as a decode-only fallback.
    return definition.options?.find((candidate) => candidate.value.length >= 2 && candidate.value[1] === rawBytes[1]);
  }

  if (definition.path === 'advanced.scaling.input_crop.rotate' && rawBytes.length >= 1) {
    // Older public profiles may not preserve the companion enable byte at 0x186c.
    return definition.options?.find((candidate) => candidate.value[0] === rawBytes[0]);
  }

  return undefined;
}

function matchPartialEnumOption(definition: SettingDefinition, rawBytes: Uint8Array) {
  const matches = (definition.options || []).filter((candidate) => {
    if (candidate.value.length < rawBytes.length) {
      return false;
    }
    return bytesEqual(rawBytes, Uint8Array.from(candidate.value.slice(0, rawBytes.length)));
  });

  return matches.length === 1 ? matches[0] : undefined;
}

export function encodeSettingValue(definition: SettingDefinition, value: SettingPrimitive): Uint8Array {
  const length = byteRangeLength(definition.byteRanges);

  switch (definition.type) {
    case 'enum': {
      const option = definition.options?.find((candidate) => candidate.label.toLowerCase() === String(value).toLowerCase());
      if (!option) {
        throw new Rt4ProfileError(`Invalid value for ${definition.label}: ${String(value)}.`);
      }
      return assertLength(definition, Uint8Array.from(option.value), length);
    }
    case 'bit':
      return Uint8Array.from([coerceBoolean(value) ? 1 : 0]);
    case 'uint8':
    case 'int8':
    case 'uint16':
    case 'int16':
    case 'uint32':
    case 'int32':
    case 'float32':
      if (definition.path === RGB_ADC_SOG_THRESHOLD_PATH) {
        return encodeRgbAdcSogThreshold(definition, value);
      }
      if (definition.path === SMPTE_2084_PQ_PATH) {
        return encodeSmpte2084Pq(definition, value);
      }
      return encodeNumericValue(definition, value);
    case 'string':
      if (definition.path === LUMACODE_PRESET_PATH) {
        const encoded = encodeCp437(String(value));
        if (encoded.length >= length) throw new Rt4ProfileError('The LumaCode preset path is too long.');
        const bytes = new Uint8Array(length);
        bytes.set(encoded);
        return bytes;
      }
      return encodeCString(String(value), length);
  }
}

function encodeSettingValueForProfile(
  definition: SettingDefinition,
  value: SettingPrimitive,
  gammaPqFactorBank: GammaPqFactorBank,
  inputModeSlot: number | null,
  optModeSlot: number | null,
  bytes: Uint8Array,
): Uint8Array {
  if (gammaPqFactorBank === 'hdr' && definition.path === GAMMA_PQ_OUTPUT_FACTOR_PATH) {
    throw new Rt4ProfileError('Output Factor cannot be edited when HDR Output is enabled.');
  }

  if (definition.path === SCALER_ASPECT_CORRECTION_PATH) {
    const aspectMetadata = optModeSlot === null
      ? inputModeAspectMetadataForSlot(inputModeSlot)
      : null;
    if (aspectMetadata?.locked) {
      throw new Rt4ProfileError(`${definition.label} is locked for this input mode.`);
    }

    const defaultDisplayValue = slotDefaultAspectCorrection(inputModeSlot, optModeSlot);
    if (defaultDisplayValue) {
      const rawValue = variableParAspectRawValue(value, defaultDisplayValue);
      if (rawValue === null) {
        throw new Rt4ProfileError(`Invalid value for ${definition.label}: ${String(value)}.`);
      }
      return Uint8Array.from([rawValue]);
    }
  }

  if (gammaPqFactorBank !== 'normal' && isGammaPqFactorPath(definition.path)) {
    return encodeGammaPqCscFactor(definition, value);
  }

  if (definition.path === RGB_ADC_SAMPLES_PER_LINE_PATH) {
    const encodedSampleRate = encodeRgbAdcSamplesPerLine(definition, value, inputModeSlot);
    if (encodedSampleRate) {
      return encodedSampleRate;
    }
  }

  if (definition.path === SDP_CONTRAST_PATH) {
    return encodeRawSdpContrast(definition, value);
  }

  if (definition.path === PROFILE_CREATED_ON_DEVICE_ID_PATH) {
    return encodeProfileCreatedOnDeviceId(definition, value);
  }

  return encodeSettingValue(definition, value);
}

function writeByteRangesForProfile(
  definition: SettingDefinition,
  gammaPqFactorBank: GammaPqFactorBank,
  inputModeSlot: number | null,
  optModeSlot: number | null,
  bytes: Uint8Array,
  changes: Record<string, SettingPrimitive>,
): ByteRange[] {
  const contextualByteRanges = contextualByteRangesForProfile(definition, bytes, inputModeSlot, optModeSlot, changes);
  if (contextualByteRanges) {
    return contextualByteRanges;
  }

  if (gammaPqFactorBank === 'normal') {
    return definition.byteRanges;
  }

  if (gammaPqFactorBank === 'hdr' && definition.path === GAMMA_PQ_INPUT_FACTOR_PATH) {
    return [{ address: HDR_GAMMA_INPUT_FACTOR_OFFSET, length: 4 }];
  }

  if (definition.path === GAMMA_PQ_INPUT_FACTOR_PATH) {
    return [{ address: CSC_GAMMA_INPUT_FACTOR_OFFSET, length: 4 }];
  }

  if (definition.path === GAMMA_PQ_OUTPUT_FACTOR_PATH) {
    return [{ address: CSC_GAMMA_OUTPUT_FACTOR_OFFSET, length: 4 }];
  }

  return definition.byteRanges;
}

function isGammaPqFactorPath(path: string): boolean {
  return path === GAMMA_PQ_INPUT_FACTOR_PATH || path === GAMMA_PQ_OUTPUT_FACTOR_PATH;
}

function encodeGammaPqCscFactor(definition: SettingDefinition, value: SettingPrimitive): Uint8Array {
  const numberValue = coerceNumber(value, definition);
  const min = definition.min ?? Number.NEGATIVE_INFINITY;
  const max = definition.max ?? Number.POSITIVE_INFINITY;

  if (numberValue < min || numberValue > max) {
    throw new Rt4ProfileError(`${definition.label} cannot encode ${numberValue}.`);
  }

  const rawValue = (numberValue - HDR_GAMMA_INPUT_FACTOR_BASE) / (definition.scale ?? 1);
  return writeNumericBytes(4, (view) => view.setInt32(0, assertIntegerRange(definition, rawValue, -0x80000000, 0x7fffffff), true));
}

function byteRangesForProfile(
  definition: SettingDefinition,
  bytes: Uint8Array,
  inputModeSlot: number | null,
  optModeSlot: number | null,
  changes: Record<string, SettingPrimitive> = {},
): ByteRange[] {
  return contextualByteRangesForProfile(definition, bytes, inputModeSlot, optModeSlot, changes) ?? definition.byteRanges;
}

function missingByteRanges(
  definition: SettingDefinition,
  bytes: Uint8Array,
  inputModeSlot: number | null,
  optModeSlot: number | null,
): ByteRange[] {
  return byteRangesForProfile(definition, bytes, inputModeSlot, optModeSlot).filter(
    (range) => range.address + range.length > bytes.length,
  );
}

function contextualByteRangesForProfile(
  definition: SettingDefinition,
  bytes: Uint8Array,
  inputModeSlot: number | null,
  optModeSlot: number | null,
  changes: Record<string, SettingPrimitive>,
): ByteRange[] | null {
  return (
    optimalModeScalerFactorByteRanges(definition.path, bytes, changes, optModeSlot) ??
    optimalModeByteRangesForSettingPath(definition.path, optModeSlot) ??
    inputModeScalerFactorByteRanges(definition.path, bytes, changes, inputModeSlot) ??
    inputModeByteRangesForSettingPath(definition.path, inputModeSlot) ??
    audioByteRangesForSettingPath(definition.path, bytes, changes)
  );
}

function optimalModeScalerFactorByteRanges(
  path: string,
  bytes: Uint8Array,
  changes: Record<string, SettingPrimitive>,
  optModeSlot: number | null,
): ByteRange[] | null {
  if (optModeSlot === null || optModeSlot < 0) {
    return null;
  }

  if (path === SCALER_HORIZONTAL_FACTOR_PATH) {
    return [{ address: rt4kArrayElementOffset('opt_scaler_hori_free_factor', optModeSlot), length: 2 }];
  }

  if (path !== SCALER_VERTICAL_FACTOR_PATH) {
    return null;
  }

  const scalingModeRaw = optimalModeScalerModeRaw(bytes, changes, optModeSlot);
  const fieldName = scalingModeRaw === SCALER_MODE_FREE_FORM_RAW ? 'opt_scaler_vert_free_factor' : 'opt_scaler_vert_ar_factor';
  return [{ address: rt4kArrayElementOffset(fieldName, optModeSlot), length: 2 }];
}

function optimalModeScalerModeRaw(
  bytes: Uint8Array,
  changes: Record<string, SettingPrimitive>,
  optModeSlot: number,
): number {
  const changedRawValue = enumRawValueFromChange(SCALER_SCALING_MODE_PATH, changes);
  if (changedRawValue !== null) {
    return changedRawValue;
  }

  return bytes[rt4kArrayElementOffset('opt_scaler_mode', optModeSlot)] ?? 0;
}

function inputModeScalerFactorByteRanges(
  path: string,
  bytes: Uint8Array,
  changes: Record<string, SettingPrimitive>,
  inputModeSlot: number | null,
): ByteRange[] | null {
  if (inputModeSlot === null || inputModeSlot < 0) {
    return null;
  }

  if (path === SCALER_HORIZONTAL_FACTOR_PATH) {
    return [{ address: rt4kArrayElementOffset('scaler_hori_free_factor', inputModeSlot), length: 2 }];
  }

  if (path !== SCALER_VERTICAL_FACTOR_PATH) {
    return null;
  }

  const scalingModeRaw = inputModeScalerModeRaw(bytes, changes, inputModeSlot);
  const fieldName = scalingModeRaw === SCALER_MODE_FREE_FORM_RAW ? 'scaler_vert_free_factor' : 'scaler_vert_ar_factor';
  return [{ address: rt4kArrayElementOffset(fieldName, inputModeSlot), length: 2 }];
}

function inputModeScalerModeRaw(
  bytes: Uint8Array,
  changes: Record<string, SettingPrimitive>,
  inputModeSlot: number,
): number {
  const changedRawValue = enumRawValueFromChange(SCALER_SCALING_MODE_PATH, changes);
  if (changedRawValue !== null) {
    return changedRawValue;
  }

  return bytes[rt4kArrayElementOffset('scaler_mode', inputModeSlot)] ?? 0;
}

function audioByteRangesForSettingPath(
  path: string,
  bytes: Uint8Array,
  changes: Record<string, SettingPrimitive>,
): ByteRange[] | null {
  switch (path) {
    case AUDIO_PREAMP_GAIN_PATH:
      return [{ address: rt4kArrayElementOffset('audio_gain', audioSourcePort(bytes, changes)), length: 1 }];
    case AUDIO_PREAMP_BALANCE_PATH:
      return [{ address: rt4kArrayElementOffset('audio_bal', audioSourcePort(bytes, changes)), length: 1 }];
    case AUDIO_INPUT_OVERRIDE_PATH:
      return [{ address: rt4kArrayElementOffset('audio_override', physicalAudioInputPort(bytes, changes)), length: 1 }];
    case AUDIO_INPUT_SWAP_PATH:
      return [{ address: rt4kArrayElementOffset('audio_swap', audioSourcePort(bytes, changes)), length: 1 }];
    case AUDIO_SAMPLE_RATE_PATH:
      return [{ address: rt4kFieldOffset('audio_sr'), length: 1 }];
    default:
      return null;
  }
}

function audioSourcePort(bytes: Uint8Array, changes: Record<string, SettingPrimitive>): number {
  const physicalPort = physicalAudioInputPort(bytes, changes);
  const overrideRaw = audioOverrideRaw(bytes, changes, physicalPort);
  if (overrideRaw === 0) {
    return physicalPort;
  }

  return AUDIO_OVERRIDE_TO_PORT.get(overrideRaw) ?? physicalPort;
}

function physicalAudioInputPort(bytes: Uint8Array, changes: Record<string, SettingPrimitive>): number {
  const changedInputPort = inputPortFromChange(changes);
  if (changedInputPort !== null) {
    return changedInputPort;
  }

  const port = bytes[rt4kFieldOffset('input_port')] ?? AUDIO_PORT_HDMI;
  return port >= 0 && port <= AUDIO_PORT_SPDIF ? port : AUDIO_PORT_HDMI;
}

function audioOverrideRaw(bytes: Uint8Array, changes: Record<string, SettingPrimitive>, physicalPort: number): number {
  const changedOverride = enumRawValueFromChange(AUDIO_INPUT_OVERRIDE_PATH, changes);
  if (changedOverride !== null) {
    return changedOverride;
  }

  return bytes[rt4kArrayElementOffset('audio_override', physicalPort)] ?? 0;
}

function inputPortFromChange(changes: Record<string, SettingPrimitive>): number | null {
  const changedInput = changes[INPUT_PATH];
  if (changedInput === undefined) {
    return null;
  }

  const inputDefinition = SETTINGS_BY_PATH.get(INPUT_PATH);
  const option = inputDefinition?.options?.find(
    (candidate) => candidate.label.toLowerCase() === String(changedInput).toLowerCase(),
  );
  const port = option?.value[3];
  return port === undefined ? null : port;
}

function enumRawValueFromChange(path: string, changes: Record<string, SettingPrimitive>): number | null {
  const changedValue = changes[path];
  if (changedValue === undefined) {
    return null;
  }

  const definition = SETTINGS_BY_PATH.get(path);
  const option = definition?.options?.find(
    (candidate) => candidate.label.toLowerCase() === String(changedValue).toLowerCase(),
  );
  return option?.value[0] ?? null;
}

function readProfileUint8(bytes: Uint8Array, path: string, inputModeSlot: number | null, fallbackOffset: number): number {
  const inputModeByteRanges = inputModeByteRangesForSettingPath(path, inputModeSlot);
  const address = inputModeByteRanges?.[0]?.address ?? fallbackOffset;
  return bytes[address] ?? 0;
}

function readUint16LE(bytes: Uint8Array, address: number): number {
  return bytes[address] | (bytes[address + 1] << 8);
}

function readInt16LE(bytes: Uint8Array, address: number): number {
  const value = readUint16LE(bytes, address);
  return value > 0x7fff ? value - 0x10000 : value;
}

function readUint32LE(bytes: Uint8Array, address: number): number {
  return (bytes[address] | (bytes[address + 1] << 8) | (bytes[address + 2] << 16) | (bytes[address + 3] << 24)) >>> 0;
}

function readInt32LE(bytes: Uint8Array, address: number): number {
  return readUint32LE(bytes, address) | 0;
}

function readFloat32LE(bytes: Uint8Array, address: number): number {
  return new DataView(bytes.buffer, bytes.byteOffset + address, 4).getFloat32(0, true);
}

function decodeNumericValue(definition: SettingDefinition, rawBytes: Uint8Array): number {
  const rawValue = decodeRawNumericValue(definition, rawBytes);
  const scale = definition.scale ?? 1;
  const offset = definition.offset ?? 0;
  return rawValue * scale + offset;
}

function decodeRawNumericValue(definition: SettingDefinition, rawBytes: Uint8Array): number {
  switch (definition.type) {
    case 'uint8':
      return rawBytes[0];
    case 'int8':
      return rawBytes[0] > 127 ? rawBytes[0] - 256 : rawBytes[0];
    case 'uint16':
      return readUint16LE(rawBytes, 0);
    case 'int16':
      return readInt16LE(rawBytes, 0);
    case 'uint32':
      return readUint32LE(rawBytes, 0);
    case 'int32':
      return readInt32LE(rawBytes, 0);
    case 'float32':
      return readFloat32LE(rawBytes, 0);
    default:
      throw new Rt4ProfileError(`${definition.label} is not numeric.`);
  }
}

function encodeNumericValue(definition: SettingDefinition, value: SettingPrimitive): Uint8Array {
  const numberValue = coerceNumber(value, definition);
  assertNumericDefinitionRange(definition, numberValue);

  const scale = definition.scale ?? 1;
  const offset = definition.offset ?? 0;
  const rawValue = (numberValue - offset) / scale;

  switch (definition.type) {
    case 'uint8':
      return Uint8Array.from([assertIntegerRange(definition, rawValue, 0, 0xff)]);
    case 'int8':
      return Uint8Array.from([assertIntegerRange(definition, rawValue, -0x80, 0x7f) & 0xff]);
    case 'uint16':
      return writeNumericBytes(2, (view) => view.setUint16(0, assertIntegerRange(definition, rawValue, 0, 0xffff), true));
    case 'int16':
      return writeNumericBytes(2, (view) => view.setInt16(0, assertIntegerRange(definition, rawValue, -0x8000, 0x7fff), true));
    case 'uint32':
      return writeNumericBytes(4, (view) => view.setUint32(0, assertIntegerRange(definition, rawValue, 0, 0xffffffff), true));
    case 'int32':
      return writeNumericBytes(4, (view) => view.setInt32(0, assertIntegerRange(definition, rawValue, -0x80000000, 0x7fffffff), true));
    case 'float32':
      return writeNumericBytes(4, (view) => view.setFloat32(0, rawValue, true));
    default:
      throw new Rt4ProfileError(`${definition.label} is not numeric.`);
  }
}

function assertNumericDefinitionRange(definition: SettingDefinition, value: number): void {
  const min = definition.min ?? Number.NEGATIVE_INFINITY;
  const max = definition.max ?? Number.POSITIVE_INFINITY;
  if (value < min || value > max) {
    throw new Rt4ProfileError(`${definition.label} cannot encode ${value}.`);
  }
}

function encodeSmpte2084Pq(definition: SettingDefinition, value: SettingPrimitive): Uint8Array {
  const numberValue = coerceNumber(value, definition);
  const min = definition.min ?? Number.NEGATIVE_INFINITY;
  const max = definition.max ?? Number.POSITIVE_INFINITY;

  if (numberValue < min || numberValue > max) {
    throw new Rt4ProfileError(`${definition.label} cannot encode ${numberValue}.`);
  }

  const rawValue = Math.round(50000 / numberValue - 50);
  return writeNumericBytes(4, (view) => view.setInt32(0, rawValue, true));
}

function encodeRgbAdcSogThreshold(definition: SettingDefinition, value: SettingPrimitive): Uint8Array {
  const numberValue = coerceNumber(value, definition);
  const min = definition.min ?? Number.NEGATIVE_INFINITY;
  const max = definition.max ?? Number.POSITIVE_INFINITY;

  if (numberValue < min || numberValue > max) {
    throw new Rt4ProfileError(`${definition.label} cannot encode ${numberValue}.`);
  }

  const rawValue = Math.round(numberValue / RGB_ADC_SOG_THRESHOLD_STEP_MV - RGB_ADC_SOG_THRESHOLD_RAW_OFFSET);
  return Uint8Array.from([assertIntegerRange(definition, rawValue, -0x80, 0x7f) & 0xff]);
}

function encodeRgbAdcSamplesPerLine(
  definition: SettingDefinition,
  value: SettingPrimitive,
  inputModeSlot: number | null,
): Uint8Array | null {
  const defaultSampleRate = inputModeDefaultSampleRateForSlot(inputModeSlot);
  if (defaultSampleRate === null) {
    return null;
  }

  const numberValue = coerceNumber(value, definition);
  const min = definition.min ?? Number.NEGATIVE_INFINITY;
  const max = definition.max ?? Number.POSITIVE_INFINITY;
  if (numberValue < min || numberValue > max) {
    throw new Rt4ProfileError(`${definition.label} cannot encode ${numberValue}.`);
  }

  const rawValue = numberValue - defaultSampleRate;
  return writeNumericBytes(2, (view) => {
    view.setInt16(0, assertIntegerRange(definition, rawValue, -0x8000, 0x7fff), true);
  });
}

function encodeRawSdpContrast(definition: SettingDefinition, value: SettingPrimitive): Uint8Array {
  const numberValue = coerceNumber(value, definition);
  const min = definition.min ?? Number.NEGATIVE_INFINITY;
  const max = definition.max ?? Number.POSITIVE_INFINITY;
  if (numberValue < min || numberValue > max) {
    throw new Rt4ProfileError(`${definition.label} cannot encode ${numberValue}.`);
  }

  return writeNumericBytes(4, (view) => {
    view.setInt32(0, assertIntegerRange(definition, numberValue, -0x80000000, 0x7fffffff), true);
  });
}

function encodeProfileCreatedOnDeviceId(definition: SettingDefinition, value: SettingPrimitive): Uint8Array {
  const normalized = String(value).trim();
  const bytes = new Uint8Array(byteRangeLength(definition.byteRanges));
  if (normalized === '' || normalized === '(empty)') {
    return bytes;
  }

  const hex = normalized.replace(/[^0-9a-f]/gi, '');
  if (hex.length !== 16) {
    throw new Rt4ProfileError(`${definition.label} must be a 16-digit hexadecimal Device ID.`);
  }

  const high = Number.parseInt(hex.slice(0, 8), 16);
  const low = Number.parseInt(hex.slice(8), 16);
  new DataView(bytes.buffer).setUint32(0, high, true);
  new DataView(bytes.buffer).setUint32(4, low, true);
  return bytes;
}

function assertIntegerRange(definition: SettingDefinition, value: number, min: number, max: number): number {
  const rounded = Math.round(value);
  if (
    !Number.isInteger(rounded) ||
    Math.abs(value - rounded) > integerEncodingTolerance(definition) ||
    rounded < min ||
    rounded > max
  ) {
    throw new Rt4ProfileError(`${definition.label} cannot encode ${value}.`);
  }
  return rounded;
}

function integerEncodingTolerance(definition: SettingDefinition): number {
  if (typeof definition.precision !== 'number') {
    return 1e-6;
  }

  const scale = Math.abs(definition.scale ?? 1);
  if (scale <= 0) {
    return 1e-6;
  }

  const displayHalfStep = 0.5 * 10 ** -definition.precision;
  return Math.max(1e-6, displayHalfStep / scale + Number.EPSILON);
}

function roundToPrecision(value: number, precision?: number): number {
  return typeof precision === 'number' ? Number(value.toFixed(precision)) : value;
}

function writeNumericBytes(length: number, write: (view: DataView) => void): Uint8Array {
  const bytes = new Uint8Array(length);
  write(new DataView(bytes.buffer));
  return bytes;
}

function formatNumericValue(definition: SettingDefinition, value: number): string {
  const rounded = typeof definition.precision === 'number' ? value.toFixed(definition.precision) : String(value);
  const normalized = definition.preserveTrailingZeros
    ? rounded
    : rounded.includes('.')
      ? rounded.replace(/\.?0+$/, '')
      : rounded;
  const signed =
    definition.showPositiveSign &&
    !normalized.startsWith('-') &&
    (definition.showPositiveSignForZero !== false || Number(normalized) !== 0)
      ? `+${normalized}`
      : normalized;
  return definition.units ? `${signed} ${definition.units}` : signed;
}

function readByteRanges(bytes: Uint8Array, ranges: ByteRange[]): Uint8Array {
  const chunks = ranges.flatMap((range) => Array.from(bytes.slice(range.address, range.address + range.length)));
  return Uint8Array.from(chunks);
}

function readAvailableByteRanges(bytes: Uint8Array, ranges: ByteRange[]): Uint8Array {
  const chunks = ranges.flatMap((range) => {
    if (range.address >= bytes.length) {
      return [];
    }
    return Array.from(bytes.slice(range.address, Math.min(range.address + range.length, bytes.length)));
  });
  return Uint8Array.from(chunks);
}

function writeByteRanges(bytes: Uint8Array, ranges: ByteRange[], value: Uint8Array): void {
  let valueOffset = 0;

  for (const range of ranges) {
    if (range.address + range.length > bytes.length) {
      throw new Rt4ProfileError(`Cannot write setting byte range ${formatByteRange(range)} to a ${bytes.length}-byte profile.`);
    }

    for (let index = 0; index < range.length; index += 1) {
      bytes[range.address + index] = value[valueOffset];
      valueOffset += 1;
    }
  }
}

function byteRangeLength(ranges: ByteRange[]): number {
  return ranges.reduce((sum, range) => sum + range.length, 0);
}

function assertLength(definition: SettingDefinition, bytes: Uint8Array, expectedLength: number): Uint8Array {
  if (bytes.length !== expectedLength) {
    throw new Rt4ProfileError(`${definition.label} encodes to ${bytes.length} bytes; expected ${expectedLength}.`);
  }
  return bytes;
}

function decodeFixedAscii(bytes: Uint8Array): string {
  return String.fromCharCode(...bytes);
}

function decodeCString(bytes: Uint8Array): string {
  const terminator = bytes.indexOf(0);
  const end = terminator === -1 ? bytes.length : terminator;
  return String.fromCharCode(...bytes.slice(0, end));
}

function encodeCString(value: string, length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  const maxLength = Math.max(length - 1, 0);
  const clipped = value.slice(0, maxLength);

  for (let index = 0; index < clipped.length; index += 1) {
    bytes[index] = clipped.charCodeAt(index) & 0xff;
  }

  return bytes;
}

function bytesEqual(left: Uint8Array, right: Uint8Array): boolean {
  if (left.length !== right.length) {
    return false;
  }

  for (let index = 0; index < left.length; index += 1) {
    if (left[index] !== right[index]) {
      return false;
    }
  }

  return true;
}

function valuesEqual(left: SettingPrimitive, right: SettingPrimitive): boolean {
  return left === right;
}

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((byte) => byte.toString(16).toUpperCase().padStart(2, '0'))
    .join(' ');
}

function formatByteRange(range: ByteRange): string {
  const start = `0x${range.address.toString(16).toUpperCase().padStart(4, '0')}`;
  if (range.length === 1) {
    return start;
  }
  const end = `0x${(range.address + range.length - 1).toString(16).toUpperCase().padStart(4, '0')}`;
  return `${start}..${end}`;
}

function coerceBoolean(value: SettingPrimitive): boolean {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  return value === 'true' || value === 'On';
}

function coerceNumber(value: SettingPrimitive, definition: SettingDefinition): number {
  const numberValue = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numberValue)) {
    throw new Rt4ProfileError(`${definition.label} must be numeric.`);
  }
  return numberValue;
}
